import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { supabase } from '../lib/supabase';
import { useColors } from '../hooks/useColors';
import { spacing } from '../theme';
import { useAuth } from '../hooks/useAuth';
import { Input } from '../components/ui/Input';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../types/navigation';
import { validatePhoneNumberSafety, safeguardProfilePhoneNumber } from '../utils/phoneNumberSafeguards';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function ProfileSetupScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { session } = useAuth();
  const colors = useColors();
  const styles = createProfileSetupScreenStyles(colors);
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateFullName = (name: string): boolean => {
    // Split the name into words and remove any extra whitespace
    const words = name.trim().split(/\s+/);
    
    // Check if we have at least 2 words (first and last name)
    if (words.length < 2) {
      setFullNameError('Please enter both your first and last name');
      return false;
    }
    
    // Take only the first and last words if there are more
    const [firstName, lastName] = [words[0], words[words.length - 1]];
    
    // Function to properly capitalize names with special patterns
    const formatName = (name: string): string => {
      // First, lowercase everything
      let formatted = name.toLowerCase();
      
      // Capitalize first letter
      formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
      
      // Handle special prefix cases (Mc, Mac, O', De, etc.)
      formatted = formatted.replace(/\b(mc|mac|o'|de|van|von|la|le|di)\w/gi, match => {
        const prefix = match.slice(0, -1);
        const letter = match.slice(-1);
        return prefix.charAt(0).toUpperCase() + prefix.slice(1) + letter.toUpperCase();
      });
      
      return formatted;
    };
    
    // Format both names
    const formattedFirstName = formatName(firstName);
    const formattedLastName = formatName(lastName);
    
    // Update the full name with proper capitalization
    setFullName(`${formattedFirstName} ${formattedLastName}`);
    setFullNameError('');
    return true;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Remove any non-digit characters including the + sign
    const digitsOnly = phone.replace(/\D/g, '');
    
    // If the number starts with '1' (country code), remove it
    const withoutCountryCode = digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;
    
    // Check if we have exactly 10 digits after removing country code
    const isValid = withoutCountryCode.length === 10;
    
    if (!isValid) {
      setPhoneError('Please enter a valid 10-digit phone number');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const formatToE164 = (phone: string): string => {
    // Remove any non-digit characters including the + sign
    const digitsOnly = phone.replace(/\D/g, '');
    
    // If the number starts with '1' (country code), remove it first
    const withoutCountryCode = digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;
    
    // Always format with +1 prefix
    return `+1${withoutCountryCode}`;
  };

  const handleSubmit = async () => {
    // Clear any existing errors
    setFullNameError('');
    setPhoneError('');
    
    // Validate inputs
    const isNameValid = validateFullName(fullName);
    
    // Enhanced phone number validation with safeguards
    const phoneValidation = validatePhoneNumberSafety(phoneNumber);
    if (!phoneValidation.isValid) {
      setPhoneError(phoneValidation.error || 'Invalid phone number');
      return;
    }
    
    if (!isNameValid) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Prepare update data with safeguards
      const updateData = {
        full_name: fullName,
        phone_number: phoneValidation.sanitizedValue,
        updated_at: new Date().toISOString(),
      };

      // Apply final safeguards
      const safeguardedData = safeguardProfilePhoneNumber(
        updateData,
        session?.user?.id || 'unknown',
        'ProfileSetupScreen'
      );
      
      const { error } = await supabase
        .from('profiles')
        .update(safeguardedData)
        .eq('id', session?.user?.id);
      
      if (error) throw error;
      
      // Navigate to main app
      navigation.reset({
        index: 0,
        routes: [{ name: 'MainTabs' }],
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      // Show generic error message
      setFullNameError('Failed to save profile information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.content}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Complete Your Profile</Text>
        </View>

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={styles.label}>FULL NAME</Text>
            <TextInput
              style={[styles.input, fullNameError ? styles.inputError : null]}
              value={fullName}
              onChangeText={(text) => {
                setFullName(text);
                if (fullNameError) setFullNameError('');
              }}
              placeholder="Enter your full name"
              autoCapitalize="words"
              autoCorrect={false}
              editable={!isLoading}
            />
            {fullNameError ? (
              <Text style={styles.errorText}>{fullNameError}</Text>
            ) : (
              <Text style={styles.hint}>
                Enter your legal first and last name (e.g., John Smith)
              </Text>
            )}
          </View>

          <Input
            label="PHONE NUMBER"
            value={phoneNumber}
            onChangeText={(text) => {
              setPhoneNumber(text);
              if (phoneError) setPhoneError('');
            }}
            placeholder="Enter your phone number"
            formatPhone={true}
            error={phoneError}
            editable={!isLoading}
            style={{ marginBottom: 0 }}
          />
          {!phoneError && (
            <Text style={styles.hint}>
              Enter your 10-digit US phone number (e.g., (555) 123-4567)
            </Text>
          )}
        </View>

        <TouchableOpacity
          style={[styles.button, isLoading && styles.buttonDisabled]}
          onPress={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator color={colors.surface} />
          ) : (
            <Text style={styles.buttonText}>Continue to App</Text>
          )}
        </TouchableOpacity>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const createProfileSetupScreenStyles = (colors: any) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    flex: 1,
    padding: spacing.lg,
  },
  header: {
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  form: {
    flex: 1,
    gap: spacing.xl,
  },
  inputContainer: {
    marginBottom: spacing.lg,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textLight,
    marginBottom: spacing.xs,
    letterSpacing: 0.75,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: spacing.md,
    fontSize: 16,
    backgroundColor: colors.white,
    color: colors.text,
  },
  inputError: {
    borderColor: colors.error,
  },
  errorText: {
    color: colors.error,
    fontSize: 14,
    marginTop: 4,
  },
  hint: {
    fontSize: 12,
    color: colors.textLight,
    marginTop: 4,
  },
  button: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 'auto',
    marginBottom: Platform.OS === 'ios' ? 34 : spacing.md, // Account for iPhone home indicator
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '600',
  },
}); 